/**
 * Web Placement Firestore Converter (Client SDK)
 *
 * Client-side converter for use with firebase/firestore (NOT firebase-admin).
 * Converts between WebPlacement TypeScript type and Firestore data.
 *
 * Supports both placement types:
 * - single: Regular web placement with funnel config
 * - ab_test: A/B test with variants referencing published placements
 *
 * CRITICAL: Uses Client SDK signature:
 * - fromFirestore(snapshot: QueryDocumentSnapshot, options?: SnapshotOptions)
 * - NOT fromFirestore(data: any) - that's Admin SDK!
 *
 * Based on placementConverter pattern from firebase-core module.
 */

import {
  FirestoreDataConverter,
  QueryDocumentSnapshot,
  SnapshotOptions,
  Timestamp,
} from 'firebase/firestore';
import type { WebPlacement, WebPlacementType } from '@modules/webplacements/types/webplacements.types';

export const webPlacementConverter: FirestoreDataConverter<WebPlacement> = {
  /**
   * Convert WebPlacement model to Firestore data
   * Called when writing to Firestore
   */
  toFirestore(webPlacement: WebPlacement): Record<string, unknown> {
    // Create a copy to avoid mutating the original
    const result: Record<string, unknown> = { ...webPlacement };

    // Remove id - Firestore keeps id in doc metadata
    delete result.id;

    // Convert ISO string dates to Firestore Timestamps
    if (typeof result.createdAt === 'string') {
      result.createdAt = Timestamp.fromDate(new Date(result.createdAt as string));
    } else if (result.createdAt instanceof Date) {
      result.createdAt = Timestamp.fromDate(result.createdAt);
    }

    if (typeof result.updatedAt === 'string') {
      result.updatedAt = Timestamp.fromDate(new Date(result.updatedAt as string));
    } else if (result.updatedAt instanceof Date) {
      result.updatedAt = Timestamp.fromDate(result.updatedAt);
    }

    if (typeof result.publishedAt === 'string') {
      result.publishedAt = Timestamp.fromDate(new Date(result.publishedAt as string));
    } else if (result.publishedAt instanceof Date) {
      result.publishedAt = Timestamp.fromDate(result.publishedAt);
    }

    // Remove undefined optional fields (Firestore doesn't like undefined)
    if (result.description === undefined) delete result.description;
    if (result.sourceKind === undefined) delete result.sourceKind;
    if (result.publishedAt === undefined) delete result.publishedAt;
    if (result.publishedBy === undefined) delete result.publishedBy;
    if (result.publishedUrl === undefined) delete result.publishedUrl;
    if (result.tags === undefined) delete result.tags;

    // A/B test fields - remove if undefined
    if (result.config === undefined) delete result.config;
    if (result.variants === undefined) delete result.variants;
    if (result.abSalt === undefined) delete result.abSalt;
    if (result.abCurrentIteration === undefined) delete result.abCurrentIteration;
    if (result.abIterationSequence === undefined) delete result.abIterationSequence;
    if (result.publishRevision === undefined) delete result.publishRevision;
    if (result.latestPublicationId === undefined) delete result.latestPublicationId;
    if (result.latestPublication === undefined) delete result.latestPublication;

    return result;
  },

  /**
   * Convert Firestore data to WebPlacement model
   * Called when reading from Firestore
   *
   * CRITICAL: Uses Client SDK signature - extract data from snapshot!
   */
  fromFirestore(
    snapshot: QueryDocumentSnapshot,
    options?: SnapshotOptions
  ): WebPlacement {
    // CRITICAL: Extract data from snapshot (Client SDK pattern)
    const data = snapshot.data(options);
    const result: Record<string, unknown> = { ...data, id: snapshot.id };

    // Convert Firestore Timestamps to ISO strings
    if ((result.createdAt as Timestamp)?.toDate) {
      result.createdAt = (result.createdAt as Timestamp).toDate().toISOString();
    }

    if ((result.updatedAt as Timestamp)?.toDate) {
      result.updatedAt = (result.updatedAt as Timestamp).toDate().toISOString();
    }

    if ((result.publishedAt as Timestamp)?.toDate) {
      result.publishedAt = (result.publishedAt as Timestamp).toDate().toISOString();
    }

    // Backward compatibility: default type to 'single' for legacy documents
    if (!result.type) {
      result.type = 'single' as WebPlacementType;
    }

    return result as unknown as WebPlacement;
  },
};

export default webPlacementConverter;
